// src/hooks/useAddToCart.js
import { useState, useEffect } from 'react';
import { getCart, addToCart } from '../services/orderApi';

const AUTO_DISMISS_MS = 3000;

/**
 * Manages the "add to cart" flow for a single product.
 *
 * @param {string|number} productId   - The product being viewed.
 * @param {object}        options
 * @param {boolean}       options.isAuthenticated  - From AuthContext.
 * @param {string}        options.role             - From AuthContext.
 * @param {boolean}       options.isCartLocked     - True when a receipt is pending review.
 * @param {Function}      options.refreshCartCount - From AuthContext.
 *
 * @returns {{
 *   isAlreadyInCart: boolean,
 *   isAdding: boolean,
 *   addedToCart: boolean,
 *   handleAddToCart: () => Promise<void>
 * }}
 */
export function useAddToCart(productId, { isAuthenticated, role, isCartLocked, refreshCartCount }) {
  const [isAlreadyInCart, setIsAlreadyInCart] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);

  // ── On mount: check whether this product is already in the cart ──────────
  useEffect(() => {
    const isEligibleClient = isAuthenticated && role === 'ROL_CLIENTE';
    if (!isEligibleClient || !productId) return;

    getCart()
      .then(res => {
        const items = res?.data?.items ?? [];
        const alreadyIn = items.some(item => String(item.productId) === String(productId));
        setIsAlreadyInCart(alreadyIn);
      })
      .catch(() => {
        // Silently ignore – if the check fails, the add will handle any duplicates server-side
      });
  }, [isAuthenticated, role, productId]);

  // ── Show success message and auto-dismiss after AUTO_DISMISS_MS ──────────
  const showSuccessMessage = () => {
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), AUTO_DISMISS_MS);
  };

  // ── Main action ───────────────────────────────────────────────────────────
  const handleAddToCart = async (product) => {
    // Prevent double-clicks or adding an already-carted product
    if (isAdding || isAlreadyInCart) return;

    // Cart locked: receipt pending admin review
    if (isCartLocked) {
      alert('Tu pago está en revisión. No puedes agregar productos hasta que el administrador apruebe o rechace el comprobante.');
      return;
    }

    setIsAdding(true);
    try {
      const price = Number(product?.precioIndividual ?? 0);
      await addToCart(productId, price);
      await refreshCartCount();
      setIsAlreadyInCart(true);
      showSuccessMessage();
    } catch (err) {
      console.error('Error al añadir al carrito:', err);
      alert('No se pudo añadir el producto al carrito. Por favor, inténtalo de nuevo.');
    } finally {
      setIsAdding(false);
    }
  };

  return { isAlreadyInCart, isAdding, addedToCart, handleAddToCart };
}
